import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { WpaCustomerContext } from './WpaCustomerContext';
import { writeSessionScope, clearSessionScope } from './wpaCartStore';
import { sortWpaCustomers } from './wpaCustomerSort';

function getCurrentCustomer() {
  try { return JSON.parse(localStorage.getItem('wpa_current_customer') || 'null'); } catch { return null; }
}
function getAvailableCustomers() {
  try {
    const list = JSON.parse(localStorage.getItem('wpa_available_customers') || '[]');
    return sortWpaCustomers(list);
  } catch { return []; }
}
function sameWpaCompanyId(sessionCompanyId, routeCompanyId) {
  if (sessionCompanyId == null || routeCompanyId == null) return false;
  const a = String(sessionCompanyId).replace(/[{}]/g, '').toLowerCase();
  const b = String(routeCompanyId).replace(/[{}]/g, '').toLowerCase();
  return a === b && a.length > 0;
}

/**
 * 包住需登入的 WPA 商店頁，負責 /api/wpa-auth/me 與客戶別狀態，讓子頁面可用 useWpaCustomer
 * （避免子頁在 me 完成前讀取空的 wpa_current_customer）。
 */
export default function WpaCommerceShell() {
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [currentCustomer, setCurrentCustomer] = useState(getCurrentCustomer);
  const [availableCustomers, setAvailableCustomers] = useState(getAvailableCustomers);
  const [customerSessionReady, setCustomerSessionReady] = useState(false);
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('wpa_token');
    if (!token) {
      setCustomerSessionReady(true);
      return undefined;
    }

    setCustomerSessionReady(false);
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch('/api/wpa-auth/me', {
          headers: {
            Authorization: `Bearer ${token}`,
            // 讓後端 displayName 按 login 後選的 UI 語言（zh-TC / zh-SC / en）回傳
            'X-WPA-Locale': currentLanguage || 'zh-TC',
          },
        });
        if (!res.ok) return;
        const json = await res.json();
        if (json.companyId != null && companyId != null && !sameWpaCompanyId(json.companyId, companyId)) {
          localStorage.removeItem('wpa_token');
          localStorage.removeItem('wpa_current_customer');
          localStorage.removeItem('wpa_available_customers');
          localStorage.removeItem('wpa_userInfo');
          clearSessionScope();
          navigate(`/${companyId}/wpa/login`, { replace: true });
          return;
        }
        if (json.identity?.id != null && json.companyId != null) {
          writeSessionScope(json.companyId, json.identity.id);
        }

        const customers = sortWpaCustomers(json.availableCustomers || []);
        const defaultId = json.defaultCustomerId;
        localStorage.setItem('wpa_available_customers', JSON.stringify(customers));
        if (cancelled) return;
        setAvailableCustomers(customers);

        const saved = getCurrentCustomer();
        const refreshed = saved && customers.find((c) => c.id === saved.id);
        if (refreshed) {
          localStorage.setItem('wpa_current_customer', JSON.stringify(refreshed));
          setCurrentCustomer(refreshed);
        } else if (!saved || !customers.some((c) => c.id === saved.id)) {
          const defaultCust = customers.find((c) => c.id === defaultId) || customers[0] || null;
          if (defaultCust) {
            localStorage.setItem('wpa_current_customer', JSON.stringify(defaultCust));
            setCurrentCustomer(defaultCust);
          }
          if (customers.length > 1 && !saved) {
            setCustomerPickerOpen(true);
          }
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setCustomerSessionReady(true);
      }
    })();

    return () => { cancelled = true; };
  }, [currentLanguage, companyId, navigate]);

  const value = useMemo(
    () => ({
      customerSessionReady,
      currentCustomer,
      availableCustomers,
      customerPickerOpen,
      setCustomerPickerOpen,
      setCurrentCustomer,
    }),
    [customerSessionReady, currentCustomer, availableCustomers, customerPickerOpen],
  );

  return (
    <WpaCustomerContext.Provider value={value}>
      <Outlet />
    </WpaCustomerContext.Provider>
  );
}
